import { useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTranslations } from 'use-intl';
import { Pause, Play, Send, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatElapsed } from '@/lib/showAndTell/formatElapsed';
import { useShieldFromOutsideDismissal } from './useShieldFromOutsideDismissal';
import { useShowAndTell } from './useShowAndTell';

const pillButton =
  'inline-flex h-7 items-center justify-center gap-1.5 rounded-full px-3 text-xs font-medium bg-background/20 hover:bg-background/30 focus-visible:outline focus-visible:outline-2 focus-visible:outline-background disabled:opacity-50';
const sendButton = 'bg-primary text-primary-foreground hover:bg-primary/90';

function PillButton({ label, onClick, primary, children }: { label: string; onClick: () => void; primary?: boolean; children: ReactNode }) {
  return (
    <button type="button" aria-label={label} onClick={onClick} className={cn(pillButton, primary && sendButton)}>
      {children}
    </button>
  );
}

/**
 * The floating recorder control (screens.md S2, option A). Rendered in a portal above Radix layers.
 * Radix subscribes to pointerdown and focusin on `document`; stopping them here keeps an open dialog
 * from treating a click on the control as an outside interaction (EC13). Marked data-sat-ignore so
 * the control's own clicks never appear in the step log.
 */
export function RecordingControl() {
  const t = useTranslations('consultantShowAndTell.control');
  const api = useShowAndTell();
  const ref = useRef<HTMLDivElement>(null);

  useShieldFromOutsideDismissal(ref);

  if (api.status === 'idle') return null;

  const time = formatElapsed(api.elapsedMs);
  let body: ReactNode;

  switch (api.status) {
    case 'recording':
    case 'paused': {
      const paused = api.status === 'paused';
      body = (
        <>
          <span className={cn('h-2.5 w-2.5 rounded-full bg-destructive', !paused && 'animate-pulse motion-reduce:animate-none')} aria-hidden="true" />
          <span className="min-w-[2.5rem] font-medium tabular-nums">{time}</span>
          {paused && <span>{t('paused')}</span>}
          {paused ? (
            <PillButton label={t('resume')} onClick={api.resume}>
              <Play className="h-3.5 w-3.5" aria-hidden="true" />
              {t('resume')}
            </PillButton>
          ) : (
            <PillButton label={t('pause')} onClick={api.pause}>
              <Pause className="h-3.5 w-3.5" aria-hidden="true" />
              {t('pause')}
            </PillButton>
          )}
          <PillButton label={t('sendLabel')} onClick={() => void api.send()} primary>
            <Send className="h-3.5 w-3.5" aria-hidden="true" />
            {t('send')}
          </PillButton>
          <PillButton label={t('cancelLabel')} onClick={api.cancel}>
            <X className="h-3.5 w-3.5" aria-hidden="true" />
          </PillButton>
        </>
      );
      break;
    }
    case 'capped':
      body = (
        <>
          <span>{t('capped', { time })}</span>
          <PillButton label={t('sendLabel')} onClick={() => void api.send()} primary>
            {t('send')}
          </PillButton>
          <PillButton label={t('discardLabel')} onClick={api.discard}>
            {t('discard')}
          </PillButton>
        </>
      );
      break;
    case 'mic-lost':
      body = (
        <>
          <span>{t('micLost', { time })}</span>
          <PillButton label={t('sendWhatIHave')} onClick={() => void api.send()} primary>
            {t('sendWhatIHave')}
          </PillButton>
          <PillButton label={t('discardLabel')} onClick={api.discard}>
            {t('discard')}
          </PillButton>
        </>
      );
      break;
    case 'uploading':
      body = (
        <>
          <span>{t('uploading', { percent: api.uploadPercent })}</span>
          <div
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={api.uploadPercent}
            aria-label={t('uploadProgress')}
            className="h-1 w-16 overflow-hidden rounded-full bg-background/30"
          >
            <div className="h-full bg-background" style={{ width: `${api.uploadPercent}%` }} />
          </div>
        </>
      );
      break;
    case 'upload-failed':
      body = (
        <>
          <span role="alert">
            {api.failure?.message ?? t('uploadFailed')}
            {api.failure?.reference && ` ${t('reference', { reference: api.failure.reference })}`}
          </span>
          <PillButton label={t('tryAgain')} onClick={() => void api.retryUpload()} primary>
            {t('tryAgain')}
          </PillButton>
          <PillButton label={t('discardLabel')} onClick={api.discard}>
            {t('discard')}
          </PillButton>
        </>
      );
      break;
  }

  return createPortal(
    <div
      ref={ref}
      role="region"
      aria-label={t('region')}
      data-testid="show-tell-pill"
      data-sat-ignore=""
      className={cn(
        'pointer-events-auto fixed bottom-4 left-1/2 z-[100] flex max-w-[calc(100vw-1rem)] -translate-x-1/2 flex-wrap items-center gap-2 rounded-full bg-foreground py-1.5 pl-3 pr-2 text-xs text-background shadow-lg',
        api.status === 'upload-failed' && 'bg-destructive text-destructive-foreground',
        api.status === 'capped' && 'bg-amber-600 text-white',
      )}
    >
      {body}
    </div>,
    document.body,
  );
}
